'use client'

import React, { useEffect, useState } from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { CloseIcon } from '../Icon'
import { Tooltip } from '../Tooltip'
import {
  actionBarWrapperStyles,
  actionIconStyles,
  actionLabelStyles,
  actionSectionStyles,
  clearButtonStyles,
  selectionLabelStyles,
  selectionSectionStyles,
} from './ActionBar.styles'

export type ActionBarAction = {
  icon?: React.ReactNode
  label: string
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  disabledTooltipLabel?: string
}

export type ActionBarProps = {
  selectionLabel: string
  onClearSelection: () => void
  actions: ActionBarAction[]
  /** colorScheme is accepted for API consistency but the ActionBar always
   *  renders in dark mode — it is a fixed chrome element of the dark design language. */
  colorScheme?: ColorScheme
}

export const ActionBar = ({
  selectionLabel,
  onClearSelection,
  actions,
  colorScheme = 'dark',
}: ActionBarProps) => {
  return (
    <Box {...actionBarWrapperStyles}>
      {/* Selection info */}
      <Box {...selectionSectionStyles(colorScheme)}>
        <Box as='span' {...selectionLabelStyles(colorScheme)}>
          {selectionLabel}
        </Box>
        <Box
          aria-label='Clear selection'
          as='button'
          onClick={onClearSelection}
          {...clearButtonStyles(colorScheme)}
        >
          <CloseIcon color='currentColor' size={12} />
        </Box>
      </Box>

      {/* Action buttons */}
      {actions.map((action, idx) => (
        <ActionButton
          action={action}
          colorScheme={colorScheme}
          isLast={idx === actions.length - 1}
          key={action.label}
        />
      ))}
    </Box>
  )
}

const ActionButton = ({
  action,
  colorScheme,
  isLast,
}: {
  action: ActionBarAction
  colorScheme: ColorScheme
  isLast: boolean
}) => {
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null)
  const hasDisabledTooltip = Boolean(
    action.disabled && action.disabledTooltipLabel
  )

  useEffect(() => {
    if (!hasDisabledTooltip) setTooltipAnchor(null)
  }, [hasDisabledTooltip])

  const showTooltip = (event: React.SyntheticEvent<HTMLElement>) => {
    if (hasDisabledTooltip) setTooltipAnchor(event.currentTarget)
  }

  const hideTooltip = () => setTooltipAnchor(null)

  return (
    <>
      <Box
        aria-disabled={action.disabled || undefined}
        aria-label={
          hasDisabledTooltip
            ? `${action.label}. ${action.disabledTooltipLabel}`
            : action.label
        }
        as='button'
        onBlur={hideTooltip}
        onClick={() => {
          if (!action.disabled) action.onClick()
        }}
        onFocus={showTooltip}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        style={isLast ? { borderRight: 'none' } : undefined}
        {...actionSectionStyles(action.isActive, colorScheme, action.disabled)}
      >
        {action.icon && (
          <Box as='span' {...actionIconStyles}>
            {action.icon}
          </Box>
        )}
        <Box as='span' {...actionLabelStyles(colorScheme)}>
          {action.label}
        </Box>
      </Box>
      <Tooltip
        anchorEl={tooltipAnchor}
        colorScheme={colorScheme}
        label={action.disabledTooltipLabel ?? ''}
        placement='top'
        variant='description'
      />
    </>
  )
}
